import {
  UserOutlined,
  QuestionCircleOutlined,
  SunOutlined,
  MoonOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import { ReactComponent as MenuBarIcon } from '../../assets/menu-bar.svg';
import { Avatar, Button, Dropdown, MenuProps, Tooltip } from 'antd';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import OrganizationsSelector from '../OrganizationsSelector/OrganizationsSelector';
import './Header.less';
import { useAuth0 } from '@auth0/auth0-react';
import { TokenService } from '../../services/secureTokenService';
import { Notify } from '../../services/notificationService';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  convertSubscriptionDataToStripeSubscription,
  openPricingPageOrSubscriptionPortal,
} from '../../services/subscription';
import ApplicationAlert from '../ApplicationAlert/ApplicationAlert';
import { AlertItem } from '../../interfaces/alert';
import NotificationBell from '../NotificationBell/NotificationBell';
import { useTheme } from '../../contexts/ThemeContext';
import PlanInfo from '../PlanInfo/PlanInfo';
import CreditInfo from './CreditInfo';
import { WalkthroughService } from '../../services/walkthroughService';
import AccountDropdown from '../AccountDropdown/AccountDropdown';
import { useRouting } from '../../hooks/useRouting';
import { useLogout } from '../../hooks/useLogout';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import { getTeamName } from '../../services/account.service';

interface HeaderProps {
  hasMenu?: boolean;
  withOrgs?: boolean;
  collapsed: boolean;
  toggleCollapsed: (collapsed: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({
  hasMenu = true,
  withOrgs = false,
  collapsed,
  toggleCollapsed,
}) => {
  const navigate = useNavigate();
  const { currentAbility } = useAbility();
  const { isOnMcpPage, homeUrl } = useRouting();
  const { themeMode, setThemeMode } = useTheme();
  const { currentTeam: currentTeamInfo } = useSelector(
    (state: RootState) => state.team
  );
  const orgName = getTeamName(currentTeamInfo?.record);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = React.useState<boolean>(false);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const {
    status: subscriptionStatus,
    subscriptionInfo,
    loading: subscriptionLoading,
  } = useSelector((state: RootState) => state.subscription);
  const subscription = subscriptionInfo?.subscription;
  const restrictManageSubscription = !currentAbility.can(
    'manage',
    Resource.Subscription,
    convertSubscriptionDataToStripeSubscription(subscription)
  );
  const { user } = useAuth0();
  const handleLogout = useLogout(homeUrl);

  const userOrgName = orgName ?? '';
  const userName = TokenService.getUserName() ?? '';
  const userEmail = TokenService.getUserEmail() ?? '';
  const userAvatar = user?.picture || TokenService.getUserAvatar();

  const handleAlertsChange = (newAlerts: AlertItem[]) => {
    setAlerts(newAlerts);
  };

  const handleSubscriptionClick = async () => {
    try {
      // Tokens are already available via TokenService (set by AuthenticationGuard)
      // No need to refresh - the auth gate ensures tokens are ready before any API calls
      await openPricingPageOrSubscriptionPortal(subscriptionStatus);
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to open subscription portal'
      );
    }
  };

  const renderSubscriptionItem = (isManageSubscription: boolean) => (
    <div className="menu-item-content">
      <span>Subscription:&nbsp;</span>
      {subscriptionLoading ? (
        <span>Loading...</span>
      ) : subscription ? (
        <PlanInfo
          subscription={subscription}
          upgradeable={!isManageSubscription}
          portalOpenable={!isManageSubscription}
        />
      ) : (
        <span>No subscription data</span>
      )}
    </div>
  );

  const dropdownItems: MenuProps['items'] = [
    {
      label: (
        <div className="account-dropdown-item">
          <Avatar src={userAvatar} icon={<UserOutlined />} />
          <div className="account-dropdown-item-content">
            <AccountInfo
              userOrgName={orgName ?? null}
              userName={userName}
              userEmail={userEmail}
            />
          </div>
        </div>
      ),
      key: 'profile',
      className: 'profile-menu-item',
    },
    {
      type: 'divider',
    },
    ...(restrictManageSubscription
      ? [
          {
            label: (
              <Tooltip title="Contact your administrator to manage your subscription">
                <div>{renderSubscriptionItem(true)}</div>
              </Tooltip>
            ),
            key: 'subscription',
            className: 'menu-item subscription-status',
          },
        ]
      : [
          {
            label: (
              <Tooltip title="Manage Subscription">
                {renderSubscriptionItem(false)}
              </Tooltip>
            ),
            key: 'subscription',
            className: 'menu-item manage-subscription',
          },
        ]),

    {
      label: <CreditInfo />,
      key: 'credits',
      className: 'menu-item credits',
    },
    {
      label: 'Theme',
      key: 'theme',
      className: 'menu-item',
      children: [
        {
          label: 'Light',
          key: 'light',
          icon: <SunOutlined />,
          onClick: () => setThemeMode('light'),
          className: `menu-item app-theme-menu-item light-theme ${themeMode === 'light' ? 'selected' : ''}`,
        },
        {
          label: 'Dark',
          key: 'dark',
          icon: <MoonOutlined />,
          onClick: () => setThemeMode('dark'),
          className: `menu-item app-theme-menu-item dark-theme ${themeMode === 'dark' ? 'selected' : ''}`,
        },
        {
          label: 'Match Browser',
          key: 'system',
          icon: <DesktopOutlined />,
          onClick: () => setThemeMode('system'),
          className: `menu-item app-theme-menu-item match-browser-theme ${themeMode === 'system' ? 'selected' : ''}`,
        },
      ],
    },
    {
      type: 'divider',
    },
    {
      label: 'Log Out',
      key: 'logout',
      onClick: handleLogout,
      className: 'menu-item',
    },
  ];

  const handleDropdownChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (open) {
      setTooltipVisible(false);
    }
  };

  const handleTooltipOpenChange = (visible: boolean) => {
    if (!isDropdownOpen) {
      setTooltipVisible(visible);
    }
  };

  return (
    <>
      <div className="header">
        <div id="header-left">
          {hasMenu && (
            <Tooltip title={collapsed ? 'Open sidebar' : 'Close sidebar'}>
              <Button
                type="text"
                className="menu-toggle-button"
                icon={<MenuBarIcon />}
                onClick={() => toggleCollapsed(!collapsed)}
              />
            </Tooltip>
          )}
          <Link id="app-logo" to={homeUrl}>
            <Logo />
          </Link>
          <div className="account-dropdown-container">
            <AccountDropdown />
          </div>
        </div>
        <div className="top-alert">
          <ApplicationAlert
            restrictManageSubscription={restrictManageSubscription}
            handleSubscriptionClick={handleSubscriptionClick}
            onAlertsChange={handleAlertsChange}
          />
        </div>
        <div id="header-right">
          <div>{withOrgs && <OrganizationsSelector />}</div>
          <NotificationBell alerts={alerts} />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'get-support',
                  label: (
                    <a
                      href="https://cirra.ai/support"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get Support
                    </a>
                  ),
                },
                {
                  key: 'documentation',
                  label: (
                    <a
                      href={'https://docs.cirra.ai'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Documentation
                    </a>
                  ),
                },
                {
                  key: 'skills-library',
                  label: (
                    <a
                      href="https://skills.cirra.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cirra AI Skills Library
                    </a>
                  ),
                },
                ...(isOnMcpPage
                  ? [
                      {
                        key: 'restart-walkthrough',
                        label: 'Restart Setup Walkthrough',
                        onClick: () => {
                          WalkthroughService.reset();
                          // Navigate directly to MCP page to avoid redirect
                          navigate('/mcp');
                          // Force a brief delay then trigger a custom event that walkthrough components can listen to
                          setTimeout(() => {
                            window.dispatchEvent(
                              new CustomEvent('restart-walkthrough')
                            );
                          }, 100);
                        },
                      },
                    ]
                  : []),
              ],
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Tooltip title="Help & Documentation">
              <Button
                type="text"
                icon={<QuestionCircleOutlined className="docs-icon" />}
                style={{ border: 'none', padding: '4px 8px' }}
              />
            </Tooltip>
          </Dropdown>

          <Dropdown
            menu={{ items: dropdownItems }}
            placement="bottomRight"
            trigger={['click']}
            onOpenChange={handleDropdownChange}
          >
            <Tooltip
              title={
                <div>
                  <TooltipContent
                    userOrgName={userOrgName}
                    userName={userName}
                    userEmail={userEmail}
                  />
                </div>
              }
              open={tooltipVisible}
              onOpenChange={handleTooltipOpenChange}
            >
              <Avatar
                className="avatar user-profile-icon"
                src={userAvatar}
                icon={<UserOutlined />}
              />
            </Tooltip>
          </Dropdown>
        </div>
      </div>
    </>
  );
};

interface AccountInfoProps {
  userOrgName: string | null;
  userName: string;
  userEmail: string;
  className?: string;
}

const AccountInfo = ({
  userOrgName,
  userName,
  userEmail,
  className,
}: AccountInfoProps) => {
  return (
    <div className={className || 'account-label'}>
      <h3>{userName}</h3>
      {userEmail && userEmail !== userName && (
        <span className="email">{userEmail}</span>
      )}
      {userOrgName ? (
        <>
          <span>
            Organization: <h5>{userOrgName}</h5>
          </span>
          {/* <span>
            {getLicense() && (
              <>
                <strong>License:</strong>{' '}
                <span
                  className={`license-info ${
                    getLicense() === OrganizationLicense.Guest
                      ? 'license-guest'
                      : getLicense() === OrganizationLicense.Full
                        ? 'license-full'
                        : ''
                  }`}
                >
                  {getLicense()}
                </span>{' '}
                <Tooltip
                  title={
                    getLicense() === OrganizationLicense.Guest
                      ? GUEST_LICENSE
                      : FULL_LICENSE
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>{' '}
                {getRoleName() && <span className="separator"></span>}
              </>
            )}
            {getRoleName() && (
              <>
                <strong>Role:</strong> {getRoleName()}
              </>
            )}
          </span> */}
        </>
      ) : (
        <span>Personal Account</span>
      )}
    </div>
  );
};

// Simplified components using the shared AccountInfo
const TooltipContent = (props: {
  userOrgName: string | null;
  userName: string;
  userEmail: string;
}) => <AccountInfo {...props} className="tooltip-content" />;

export default Header;
